import { DownloadOutlined } from "@ant-design/icons";
import { Button, Space, Typography, message } from "antd";
import QRCode from "qrcode";
import React, { useEffect, useRef, useState } from "react";
import { canvasToBlob, downloadBlob } from "../utils";

const { Text, Title } = Typography;

type QrCodePanelProps = {
  value: string;
  title?: string;
  description?: string;
  fileNamePrefix?: string;
  size?: number;
};

export const QrCodePanel = (props: QrCodePanelProps) => {
  const {
    value,
    title = "QR Code",
    description = "Download the QR code to share or print.",
    fileNamePrefix = "qr-code",
    size = 180,
  } = props;

  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    let cancelled = false;

    const renderQr = async () => {
      if (!value || !canvasRef.current) {
        setReady(false);
        return;
      }

      try {
        await QRCode.toCanvas(canvasRef.current, value, {
          width: size,
          margin: 1,
          errorCorrectionLevel: "M",
          color: { dark: "#0f172a", light: "#ffffff" },
        });
        if (!cancelled) setReady(true);
      } catch {
        if (!cancelled) {
          setReady(false);
          message.error("Failed to generate QR code");
        }
      }
    };

    renderQr();
    return () => {
      cancelled = true;
    };
  }, [value, size]);

  const handleDownloadJpg = async () => {
    if (!canvasRef.current || !ready) {
      message.error("QR code is not ready yet");
      return;
    }

    const blob = await canvasToBlob(canvasRef.current, "image/jpeg");
    if (!blob) {
      message.error("Failed to download JPG");
      return;
    }

    downloadBlob(blob, `${fileNamePrefix}.jpg`);
  };

  const handleDownloadSvg = async () => {
    if (!value) {
      message.error("QR code value is not available");
      return;
    }

    try {
      const svg = await QRCode.toString(value, {
        type: "svg",
        margin: 1,
        errorCorrectionLevel: "M",
        color: { dark: "#0f172a", light: "#ffffff" },
      });
      downloadBlob(new Blob([svg], { type: "image/svg+xml" }), `${fileNamePrefix}.svg`);
    } catch {
      message.error("Failed to download SVG");
    }
  };

  if (!value) {
    return (
      <Space direction="vertical" size={16} className="w-100">
        <Title level={5} className="ag-primary mb-0">
          {title}
        </Title>
        <Text type="secondary">QR code value is not available.</Text>
      </Space>
    );
  }

  return (
    <Space direction="vertical" size={16} className="w-100">
      <Title level={5} className="ag-primary mb-0">
        {title}
      </Title>

      <Space size={24} align="start" wrap>
        <div
          style={{
            padding: 16,
            border: "1px solid #e5e7eb",
            borderRadius: 8,
            background: "#fff",
            lineHeight: 0,
          }}
        >
          <canvas ref={canvasRef} width={size} height={size} />
        </div>

        <Space direction="vertical" size={12}>
          <Text>{description}</Text>
          <Space wrap>
            <Button type="primary" icon={<DownloadOutlined />} onClick={handleDownloadJpg} disabled={!ready}>
              Download JPG
            </Button>
            <Button icon={<DownloadOutlined />} onClick={handleDownloadSvg} disabled={!ready}>
              Download SVG
            </Button>
          </Space>
        </Space>
      </Space>
    </Space>
  );
};
